import { put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import uniqid from 'uniqid';
import { PSYCHOGRAPHIC_PREFIX, APPS_MAP } from 'src/constants/config';

import { isEmpty } from 'src/utils/misc';
import { isTrialUser } from 'src/utils/AuthHelperMethods';
import { supportsSegmentationAndCompare } from 'src/utils/features';
import { getProductScope } from '@discover-frontend/utils';
import {
  // GET_AUDIENCE_REQUEST, DO NOT REMOVE, IT WILL BE NEEDED
  ADD_TOAST,
  CLONE_AUDIENCE_FAILURE,
  CLONE_AUDIENCE_REQUEST,
  CLONE_AUDIENCE_SUCCESS,
  CREATE_AUDIENCE_FAILURE,
  CREATE_AUDIENCE_REQUEST,
  CREATE_AUDIENCE_SUCCESS,
  CREATE_FOLDER_FAILURE,
  CREATE_FOLDER_REQUEST,
  CREATE_FOLDER_SUCCESS,
  DELETE_ITEM_FAILURE,
  DELETE_ITEM_REQUEST,
  DELETE_ITEM_SUCCESS,
  EXPLORE_FOLDER_REQUEST,
  EXPLORE_FOLDER_SUCCESS,
  EXPLORE_TARGET_FOLDERS_REQUEST,
  EXPLORE_TARGET_FOLDERS_SUCCESS,
  GET_AUDIENCE_FAILURE,
  GET_AUDIENCE_SUCCESS,
  GET_FOLDER_FAILURE,
  GET_FOLDER_REQUEST,
  GET_FOLDER_SUCCESS,
  GET_QUERY_PREVIEW_REQUEST,
  GET_QUERY_PREVIEW_SUCCESS,
  GET_REPOSITORY_FAILURE,
  GET_REPOSITORY_REQUEST,
  GET_REPOSITORY_SUCCESS,
  GET_TARGET_FOLDERS_FAILURE,
  GET_TARGET_FOLDERS_REQUEST,
  GET_TARGET_FOLDERS_SUCCESS,
  GET_TARGET_FOLDER_FAILURE,
  GET_TARGET_FOLDER_REQUEST,
  GET_TARGET_FOLDER_SUCCESS,
  HIDE_CREATE_AUDIENCE_MODAL,
  MOVE_ITEM_FAILURE,
  MOVE_ITEM_REQUEST,
  MOVE_ITEM_SUCCESS,
  RENAME_ITEM_FAILURE,
  RENAME_ITEM_REQUEST,
  RENAME_ITEM_SUCCESS,
  SAVE_AUDIENCE_FAILURE,
  SAVE_AUDIENCE_REQUEST,
  SAVE_AUDIENCE_SUCCESS,
  SHOW_EDIT_AUDIENCE_MODAL,
  TOGGLE_ALL_ITEM_SELECTION_REQUEST,
  TOGGLE_ALL_ITEM_SELECTION_SUCCESS,
  TOGGLE_ITEM_SELECTION_REQUEST,
  TOGGLE_ITEM_SELECTION_SUCCESS,
  UPDATE_WITH_CREATED_FOLDER,
  REPOSITORY_LOAD_MORE_RESULTS_REQUEST,
  REPOSITORY_LOAD_MORE_RESULTS_SUCCESS,
  REPOSITORY_LOAD_MORE_RESULTS_FAILURE,
  OPEN_SAVE_MODAL,
  SHOW_MODAL,
  SHARE_AUDIENCE_SUCCESS,
  SHARE_AUDIENCE_FAILURE,
  SHARE_AUDIENCE_REQUEST,
  ADD_AUDIENCE_TO_COMPARE_REQUEST,
  ADD_AUDIENCE_TO_COMPARE_SUCCESS,
  ADD_AUDIENCE_TO_COMPARE_FAILURE,
  ADD_APPLICATION_DROPPING_ENTITY_REQUEST,
  UPDATE_APPLICATION_DROPPING_ENTITIES_REQUEST,
  OPEN_COMPARE_AFTER_SAVE,
  GET_QUERY_MEMBERSHIP_REQUEST,
  GET_ALL_FOLDERS_REQUEST,
  GET_ALL_FOLDERS_SUCCESS,
  GET_ALL_FOLDERS_FAILURE,
  UPDATE_REPOSITORY_WITH_FOLDERS,
  SET_COMPARE_ERROR,
  UPDATE_TAB_LOADING_STATUS_SUCCESS,
  CHANGE_SELECTED_APPLICATION_TAB,
} from '../constants/action-types';
import * as api from '../services/repositoryApi';
import * as engineApi from '../services/engineApi';
import {
  requestRepository,
  hideModal,
  addApplicationTab,
  addToast,
  addApplicationDroppingEntity,
  hideCreateAudienceModal,
  clearAllQueriesRequest,
  collapseRepository,
  changeDataSourceRequest,
} from '../actions';
import {
  findAndAddChild,
  findAndRemoveChild,
  getUpdatedRepo,
  getItemWithUpdatedPath,
  findParentIdByPath,
  findAndUpdateLabel,
  prepareNewAudience,
  setFolderChildren,
} from '../operations/repository';

import {
  mapQueryBlocksWithUniqueIdAndFormat,
  getQueryItemsIds,
  mapConversationQueries,
  mergeMetadataWithQuerySources,
} from '../operations/query';

import sagaFetch from './SagaFetch';
import translations from '../lang';
import { getCurrentProductDataSources } from '../selectors/myCompany';
import {
  getTabsFromLocalStorage,
  setTabsInLocalStorage,
} from '../utils/TabsLocalStorage';

function* handleGetAudienceSuccess(action) {
  const { audience } = action;

  yield put(changeDataSourceRequest(audience.metadata.data_source));
  window.dispatchEvent(new CustomEvent('REFRESH_TAXONOMY'));
}

function* handleCreateItemSuccess(action) {
  const { data: new_item, parent_id, path, isSearch, clone } = action;
  const currentData = yield select((state) => state.repository.data);
  const isFolder = action.data.type === 'folder';
  const currentFilter = isFolder
    ? 'my'
    : yield select((state) => state.repository.filter);
  const tree = yield select((state) => state.repository.data[currentFilter]);

  const isRecentFolder =
    currentFilter === 'recent' && action.data.type === 'folder';

  if (!isRecentFolder) {
    const updatedFilteredData = findAndAddChild(tree, parent_id, new_item);

    const updatedData = getUpdatedRepo(
      currentData,
      updatedFilteredData,
      currentFilter
    );

    yield put({
      type: UPDATE_WITH_CREATED_FOLDER,
      data: updatedData ? updatedData : currentData,
      filter: isFolder ? currentFilter : undefined,
      currentFolder: isFolder ? (!isSearch ? parent_id : 0) : undefined,
      path: isFolder && !isSearch && !isEmpty(path) ? path : undefined,
    });
  }

  yield put(hideModal());

  const messageKey = isFolder
    ? 'CREATE_FOLDER'
    : clone
    ? 'CLONE_AUDIENCE'
    : 'CREATE_FOLDER';

  yield put({
    type: ADD_TOAST,
    id: new_item.id,
    toastProps: {
      status: 'success',
      message: messageKey,
    },
  });
}

function* fetchRepository(action) {
  const { repoFilterType, q } = action;
  const currentPagination = yield select(
    (state) => state.repository.pagination
  );
  const productDataSources = yield select(getCurrentProductDataSources);
  const source = isEmpty(productDataSources) && 'twitter';

  const { response, error } = yield sagaFetch(api.fetchRepositoryApi, {
    mode: repoFilterType,
    q,
    pagination: currentPagination[repoFilterType],
    source,
  });

  if (!error) {
    if (repoFilterType === 'recent' && response.data.length === 0) {
      yield put(requestRepository('template'));
    } else {
      const currentData = yield select((state) => state.repository.data);
      const filter = q ? 'search' : repoFilterType;
      yield put({
        type: GET_REPOSITORY_SUCCESS,
        response: getUpdatedRepo(currentData, response.data, filter),
        total_count: response.total_count,
      });

      if (
        repoFilterType === 'my' &&
        currentPagination[repoFilterType].page === 1
      ) {
        const { response: firstFolderResponse, error: firstFolderError } =
          yield sagaFetch(api.fetchRepositoryApi, {
            mode: 'folders',
            pagination: {
              page: 1,
              limit: 1,
            },
            q,
            flat: q !== undefined,
          });

        if (!firstFolderError) {
          if (firstFolderResponse.total_count !== 0) {
            const { response: foldersResponse } = yield sagaFetch(
              api.fetchRepositoryApi,
              {
                mode: 'folders',
                pagination: {
                  page: 1,
                  limit: firstFolderResponse.total_count,
                },
                q,
                flat: q !== undefined,
              }
            );

            if (foldersResponse) {
              return yield put({
                type: UPDATE_REPOSITORY_WITH_FOLDERS,
                response: foldersResponse.data,
              });
            }
          }
        }
      }
      yield put({
        type: UPDATE_REPOSITORY_WITH_FOLDERS,
        response: [],
      });
    }
  } else {
    yield put({ type: GET_REPOSITORY_FAILURE, message: error.message });
  }
}

function* fetchTargetFolders(action) {
  const { q, pagination } = action;
  const productDataSources = yield select(getCurrentProductDataSources);
  const source = isEmpty(productDataSources) && 'twitter';
  const currentData = yield select((state) => state.repository.data);
  const currentMyData = yield select((state) => state.repository.data.my);
  const currentTargetSearch = yield select(
    (state) => state.repository.targetQ && state.repository.targetQ.length > 0
  );

  if (currentMyData.length > 0 && !currentTargetSearch) {
    return yield put({
      type: GET_TARGET_FOLDERS_SUCCESS,
      response: currentData,
    });
  }

  const { response, error } = yield sagaFetch(api.fetchRepositoryApi, {
    mode: 'my',
    q,
    pagination,
    source,
  });

  if (!error) {
    let folders = [];
    const filter = currentTargetSearch ? 'search' : 'my';
    const { response: firstFolderResponse, error: firstFolderError } =
      yield sagaFetch(api.fetchRepositoryApi, {
        mode: 'folders',
        pagination: {
          page: 1,
          limit: 1,
        },
        q,
        flat: q !== undefined,
      });

    if (!firstFolderError) {
      if (firstFolderResponse.total_count !== 0) {
        const { response: foldersResponse } = yield sagaFetch(
          api.fetchRepositoryApi,
          {
            mode: 'folders',
            pagination: {
              page: 1,
              limit: firstFolderResponse.total_count,
            },
            q,
            flat: q !== undefined,
          }
        );

        if (foldersResponse) {
          folders = foldersResponse.data;
        }
      }
    }
    const results = [...folders].concat(response.data);
    return yield put({
      type: GET_TARGET_FOLDERS_SUCCESS,
      total_count: response.total_count,
      response: getUpdatedRepo(currentData, results, filter),
    });
  }
  yield put({ type: GET_TARGET_FOLDERS_FAILURE, message: error.message });
}

function* fetchFolder(action) {
  const { id } = action;
  const productDataSources = yield select(getCurrentProductDataSources);
  const source = isEmpty(productDataSources) && 'twitter';

  const { response: responseFolders, error: errorFolders } = yield sagaFetch(
    api.fetchFolderApi,
    id,
    'folder'
  );

  const currentFilter = yield select((state) => state.repository.filter);
  const currentData = yield select((state) => state.repository.data);
  const currentFilteredData = yield select(
    (state) => state.repository.data[currentFilter]
  );
  let response = [];
  if (!errorFolders) {
    response = [...responseFolders.data];

    const { response: responseRepository, error: errorRepository } =
      yield sagaFetch(api.fetchRepositoryApi, {
        mode: 'folder-detail',
        parent: id,
        source,
      });

    if (!errorRepository) {
      response = [...response, ...responseRepository.data];

      const updateFilteredData = currentFilteredData.map((item) => {
        return setFolderChildren(id, item, response);
      });

      const updateData = getUpdatedRepo(
        currentData,
        updateFilteredData,
        currentFilter
      );
      return yield put({
        type: GET_FOLDER_SUCCESS,
        id,
        response: updateData,
      });
    }
    yield put({ type: GET_FOLDER_FAILURE, message: errorRepository.message });
  }

  yield put({ type: GET_FOLDER_FAILURE, message: errorFolders.message });
}

function* fetchTargetFolder(action) {
  const { id } = action;
  const productDataSources = yield select(getCurrentProductDataSources);
  const source = isEmpty(productDataSources) && 'twitter';

  const { response: responseFolders, error: errorFolders } = yield sagaFetch(
    api.fetchFolderApi,
    id,
    'folder'
  );

  const currentTargetSearch = yield select(
    (state) => state.repository.targetQ && state.repository.targetQ.length > 0
  );

  const currentFilter = currentTargetSearch ? 'search' : 'my';
  const currentData = yield select((state) => state.repository.data);
  const currentFilteredData = yield select(
    (state) => state.repository.data[currentFilter]
  );

  let response = [];
  if (!errorFolders) {
    response = [...responseFolders.data];

    const { response: responseRepository, error: errorRepository } =
      yield sagaFetch(api.fetchRepositoryApi, {
        mode: 'folder-detail',
        parent: id,
        source,
      });

    if (!errorRepository) {
      response = [...response, ...responseRepository.data];

      const updateFilteredData = currentFilteredData.map((item) => {
        return setFolderChildren(id, item, response);
      });

      const updateData = getUpdatedRepo(
        currentData,
        updateFilteredData,
        currentFilter
      );
      return yield put({
        type: GET_TARGET_FOLDER_SUCCESS,
        id,
        response: updateData,
      });
    }

    yield put({
      type: GET_TARGET_FOLDER_FAILURE,
      message: errorRepository.message,
    });
  }

  yield put({ type: GET_TARGET_FOLDER_FAILURE, message: errorFolders.message });
}

function* fetchFolders() {
  const { response: firstItemResponse, error: firstItemError } =
    yield sagaFetch(api.fetchRepositoryApi, {
      mode: 'folders',
      pagination: {
        page: 1,
        limit: 1,
      },
      flat: true,
    });

  if (!firstItemError) {
    if (firstItemResponse.total_count === 0) {
      return yield put({
        type: GET_ALL_FOLDERS_SUCCESS,
        data: [],
      });
    }
    const { response } = yield sagaFetch(api.fetchRepositoryApi, {
      mode: 'folders',
      pagination: {
        page: 1,
        limit: firstItemResponse.total_count,
      },
      flat: true,
    });

    if (response) {
      return yield put({
        type: GET_ALL_FOLDERS_SUCCESS,
        data: response.data,
      });
    }
  }
  yield put({ type: GET_ALL_FOLDERS_FAILURE, message: firstItemError.message });
}
function* exploreFolder(action) {
  const { id } = action;

  yield put({
    type: EXPLORE_FOLDER_SUCCESS,
    id: id === 0 ? null : id,
  });
}

function* exploreTargetFolders(action) {
  const { id } = action;

  yield put({
    type: EXPLORE_TARGET_FOLDERS_SUCCESS,
    id: id === 0 ? null : id,
  });
}

function* fetchCreateFolder(action) {
  const { data, path, isSearch } = action;
  data.scope = getProductScope();
  const { response, error } = yield sagaFetch(api.fetchCreateFolderApi, data);

  if (response) {
    yield put({
      type: CREATE_FOLDER_SUCCESS,
      data: response.data,
      parent_id: data.parent_id,
      path,
      isSearch,
    });
  } else {
    yield put({ type: CREATE_FOLDER_FAILURE, message: error.message });
  }
}

function* fetchDeleteItem(action) {
  const { items, itemType } = action;
  const itemsIds = items.map((item) => item.id);
  const { error } = yield sagaFetch(api.fetchDeleteItemApi, itemsIds);

  if (!error) {
    yield put({
      type: DELETE_ITEM_SUCCESS,
      itemType,
      items,
    });
  } else {
    yield put({
      type: DELETE_ITEM_FAILURE,
      message: error,
    });
    yield put(hideModal());
    yield put({
      type: ADD_TOAST,
      id: items[0].id,
      toastProps: {
        status: 'error',
        message: error,
      },
    });
  }
}

function* handleDeleteItemSuccess(action) {
  const { items, itemType } = action;
  const itemsIds = items.map((item) => item.id);

  /* Fetch User Tabs */
  const email = yield select((state) => state.me.data.email);
  const localStorageTabs = getTabsFromLocalStorage(email);

  const currentData = yield select((state) => state.repository.data);
  const currentFilter = yield select((state) => state.repository.filter);
  const currentMyData = yield select(
    (state) => state.repository.data[currentFilter]
  );

  const parent_removing_id = findParentIdByPath(items[0]);

  const updatedDataWithoutRemoved = items.reduce((acc, item) => {
    return findAndRemoveChild(acc, parent_removing_id, item.id);
  }, currentMyData);

  const updatedData = getUpdatedRepo(
    currentData,
    updatedDataWithoutRemoved,
    currentFilter
  );

  yield put({
    type: UPDATE_WITH_CREATED_FOLDER,
    data: updatedData ? updatedData : currentData,
  });

  if (
    localStorageTabs &&
    Array.isArray(localStorageTabs) &&
    localStorageTabs.length
  ) {
    const availableTabs = localStorageTabs.filter(
      (tab) =>
        (tab.app === 'compare' &&
          tab.audience &&
          tab.audience.every((a) => !itemsIds.includes(a.id))) ||
        (tab.app !== 'compare' &&
          tab.audience &&
          !itemsIds.includes(tab.audience.id))
    );

    yield put({
      type: UPDATE_TAB_LOADING_STATUS_SUCCESS,
      tabs: availableTabs,
    });
    if (!isEmpty(availableTabs)) {
      yield put({
        type: CHANGE_SELECTED_APPLICATION_TAB,
        id: availableTabs[0].id,
      });
    }
    setTabsInLocalStorage(availableTabs, email);
  }

  window.dispatchEvent(
    new CustomEvent('CLEAR_MULTIPLE_SELECTION_REPOSITORY_ITEMS')
  );

  const id = uniqid();
  yield put(hideModal());
  yield put({
    type: ADD_TOAST,
    id,
    toastProps: {
      status: 'success',
      message: `DELETE_${itemType.toUpperCase()}`,
    },
  });
}

function* fetchCloneAudience(action) {
  const { id, data } = action;
  data.scope = getProductScope();
  const { response, error } = yield sagaFetch(
    api.fetchCloneAudienceApi,
    id,
    data
  );

  if (!error && response) {
    yield put({
      type: CLONE_AUDIENCE_SUCCESS,
      data: {
        ...response.data,
        metadata: data.metadata,
      },
      parent_id: data.parent_id,
      clone: true,
    });
  }

  if (error) {
    yield put({ type: CLONE_AUDIENCE_FAILURE, message: error.message });
    yield put({
      type: ADD_TOAST,
      id,
      toastProps: {
        status: 'error',
        message: error.message,
      },
    });
  }
}

function* fetchMoveItem(action) {
  const { id, data, movingItem, destinationItem } = action;
  data.scope = getProductScope();
  const { error } = yield sagaFetch(api.fetchMoveItemApi, id, data);

  if (!error) {
    yield put({
      type: MOVE_ITEM_SUCCESS,
      movingItem,
      destinationItem,
      parent_id: data.parent_id,
    });
  } else {
    yield put({ type: MOVE_ITEM_FAILURE, message: error.message });
  }
}

function* handleMoveItemSuccess(action) {
  const { parent_id, movingItem, destinationItem } = action;

  const currentData = yield select((state) => state.repository.data);
  const currentFilter = yield select((state) => state.repository.filter);
  const currentMyData = yield select(
    (state) => state.repository.data[currentFilter]
  );

  const parent_removing_id = findParentIdByPath(movingItem);

  const updatedDataWithoutRemoved = findAndRemoveChild(
    currentMyData,
    parent_removing_id,
    movingItem.id
  );

  const updatedDataWithMoved = findAndAddChild(
    updatedDataWithoutRemoved,
    parent_id,
    getItemWithUpdatedPath(movingItem, destinationItem)
  );

  const updatedData = getUpdatedRepo(
    currentData,
    updatedDataWithMoved,
    currentFilter
  );

  yield put({
    type: UPDATE_WITH_CREATED_FOLDER,
    data: updatedData ? updatedData : currentData,
  });

  yield put(hideModal());
  yield put({
    type: ADD_TOAST,
    id: movingItem.id,
    toastProps: {
      status: 'success',
      message: 'MOVE',
    },
  });
}

function* fetchRenameItem(action) {
  const { id, newLabel } = action;

  const data = { id, newLabel };
  const { error } = yield sagaFetch(api.fetchRenameItemApi, id, {
    label: newLabel,
    scope: getProductScope(),
  });

  if (!error) {
    window.dispatchEvent(
      new CustomEvent('AUDIENCE_LABEL_CHANGED', {
        detail: {
          id,
          label: newLabel,
        },
      })
    );
    return yield put({
      type: RENAME_ITEM_SUCCESS,
      data,
    });
  }

  return yield put({
    type: RENAME_ITEM_FAILURE,
    message: error.message,
  });
}

function* handleRenameItemSuccess(action) {
  const { data } = action;

  const currentData = yield select((state) => state.repository.data);
  const currentFilter = yield select((state) => state.repository.filter);
  const currentMyData = yield select(
    (state) => state.repository.data[currentFilter]
  );

  const updatedDataWithoutRenamed = findAndUpdateLabel(
    currentMyData,
    data.id,
    data.newLabel
  );

  const updatedData = getUpdatedRepo(
    currentData,
    updatedDataWithoutRenamed,
    currentFilter
  );

  yield put({
    type: UPDATE_WITH_CREATED_FOLDER,
    data: updatedData ? updatedData : currentData,
  });

  const id = uniqid();
  yield put(hideModal());
  yield put({
    type: ADD_TOAST,
    id,
    toastProps: {
      status: 'success',
      message: 'RENAME',
    },
  });
}

function* handleItemToggleSelectionRequest(action) {
  const { id } = action;
  const currentSelectedIds = yield select(
    (state) => state.repository.currentSelectedIds
  );

  const newSelectedids = currentSelectedIds.includes(id)
    ? currentSelectedIds.filter((item) => item !== id)
    : [...currentSelectedIds, id];

  yield put({
    type: TOGGLE_ITEM_SELECTION_SUCCESS,
    data: newSelectedids,
  });
}

function* handleItemToggleAllSelectionRequest(action) {
  const { ids, mode } = action;

  const newSelectedids = mode ? [...ids] : [];

  yield put({
    type: TOGGLE_ALL_ITEM_SELECTION_SUCCESS,
    data: newSelectedids,
  });
}

// function* handleGetAudienceRequest(action) { // DO NOT REMOVE, IT WILL BE NEEDED
//   const { metadata } = action;

//   const queryFormatted = mapQueryBlocksWithUniqueIdAndFormat(
//     metadata.target_query
//   );
//   const ids = getQueryItemsIds(queryFormatted);
//   const { response: metadataResponse, error: metadataError } = yield sagaFetch(
//     engineApi.fetchQueryItemsMetadata,
//     ids
//   );

//   if (!metadataError) {
//     const audience = {
//       ...metadata,
//       target_query: queryFormatted,
//     };

//     const queryEntitiesInfos = {
//       ...arrToObj(metadataResponse),
//     };

//     return yield put({
//       type: GET_AUDIENCE_SUCCESS,
//       audience,
//       queryEntitiesInfos,
//     });
//   }

//   return yield put({
//     type: GET_AUDIENCE_FAILURE,
//     error: true,
//     message: metadataError.message,
//   });
// }

function* handleCreateAudienceRequest(action) {
  const { queryData, app, silentMode } = action;
  let dataSource = queryData?.data_source;

  if (!dataSource)
    dataSource = yield select((state) => state.queryBuilder.dataSource);

  const productDataSources = yield select(getCurrentProductDataSources);
  const filter = !isEmpty(productDataSources) ? dataSource : 'recent';
  const mappedBase = queryData.base_query;
  if (Array.isArray(mappedBase?.query_block?.arguments_list)) {
    if (
      mappedBase.query_block.arguments_list[0].id.startsWith(
        PSYCHOGRAPHIC_PREFIX
      )
    ) {
      mappedBase.query_block.arguments_list[0].id =
        mappedBase.query_block.arguments_list[0].id.slice(
          PSYCHOGRAPHIC_PREFIX.length
        );
    }
  }
  const audience = {
    ...queryData,
    base_query: mappedBase,
    label: queryData.target_label || queryData.label,
    data_source: dataSource,
  };
  const newAudience = prepareNewAudience(audience);
  newAudience.scope = getProductScope();
  const { response, error } = yield sagaFetch(
    api.fetchCreateAudienceApi,
    newAudience
  );

  if (!error) {
    yield put({
      type: HIDE_CREATE_AUDIENCE_MODAL,
    });
    yield put(clearAllQueriesRequest());
    yield put(collapseRepository());

    if (!silentMode) {
      yield put(requestRepository(filter));

      if (!isTrialUser()) {
        yield put({
          type: ADD_TOAST,
          id: uniqid(),
          toastProps: {
            status: 'success',
            message: 'CREATE_AUDIENCE',
          },
        });
      }
    }

    const audienceInTabFormat = {
      label: response.data.label,
      id: response.data.id,
      persistence: 'temporary',
      metadata: newAudience,
    };

    yield put(addApplicationTab(audienceInTabFormat, app || 'analyze'));

    return yield put({
      type: CREATE_AUDIENCE_SUCCESS,
    });
  }

  return yield put({
    type: CREATE_AUDIENCE_FAILURE,
    error: true,
    message: error.message,
  });
}

const mapMemberships = (memberships) => {
  return memberships
    ? memberships.map((m) => ({
        id: m.id,
        type: 'membership',
        class: m.query_type,
        label: m.label,
      }))
    : [];
};

function* handleQueryPreviewRequest(action) {
  const { selectedItem } = action;

  const targetQueryFormatted = mapQueryBlocksWithUniqueIdAndFormat(
    selectedItem.metadata.target_query
  );

  const baseQueryBlock = selectedItem.metadata.base_query
    ? selectedItem.metadata.base_query.query_block
    : null;

  const baseQueryFormatted =
    baseQueryBlock && mapQueryBlocksWithUniqueIdAndFormat(baseQueryBlock);

  if (selectedItem.metadata.audience_type === 'conversation') {
    const conversationQueries = {};
    const tree = mapConversationQueries(
      selectedItem.metadata.target_query,
      true,
      conversationQueries
    );
    const audience = {
      ...selectedItem,
      metadata: {
        ...selectedItem.metadata,
        target_query: tree,
      },
      queryBuilderType: 'conversation',
    };

    return yield put({
      type: GET_QUERY_PREVIEW_SUCCESS,
      audience,
      conversationQueries,
    });
  } else {
    const emptyResponse = { response: { data: [] } };

    const targetQueryIds = getQueryItemsIds(targetQueryFormatted);
    const baseQueryIds = getQueryItemsIds(baseQueryFormatted);
    const metadataIds = [...targetQueryIds, ...baseQueryIds];

    const { response: metadataResponse, error: metadataError } =
      metadataIds.length > 0
        ? yield sagaFetch(
            engineApi.fetchQueryItemsMetadata,
            metadataIds,
            selectedItem.metadata.data_source
          )
        : emptyResponse;

    const membershipIds = getQueryItemsIds(targetQueryFormatted, 'membership');
    const { response: membershipResponse, error: membershipError } =
      membershipIds.length > 0
        ? yield sagaFetch(api.fetchGetMembershipsApi, membershipIds)
        : emptyResponse;

    if (!metadataError && !membershipError) {
      const audience = {
        ...selectedItem,
        metadata: {
          ...selectedItem.metadata,
          target_query: targetQueryFormatted,
        },
        queryBuilderType: 'default',
      };

      return yield put({
        type: GET_QUERY_PREVIEW_SUCCESS,
        audience,
        queryEntitiesInfos: [
          ...mergeMetadataWithQuerySources(
            metadataResponse.data,
            targetQueryFormatted,
            baseQueryFormatted
          ),
          ...mapMemberships(membershipResponse.data),
        ],
      });
    }
  }
}

function* fetchSaveAudienceRequest(action) {
  const { id, data } = action;
  data.scope = getProductScope();
  const { error } = yield sagaFetch(api.fetchSaveAudienceApi, id, data);

  if (!error) {
    yield put({
      type: SAVE_AUDIENCE_SUCCESS,
      savedId: id,
      data,
    });
    yield put(hideModal());
    yield put(requestRepository('my'));
    yield put({
      type: ADD_TOAST,
      id,
      toastProps: {
        status: 'success',
        message: 'SAVE',
      },
    });
    window.dispatchEvent(
      new CustomEvent('AUDIENCE_LABEL_CHANGED', {
        detail: {
          id,
          label: action.data.label,
        },
      })
    );
    window.dispatchEvent(
      new CustomEvent('AUDIENCE_SAVED', {
        detail: {
          id,
        },
      })
    );
  } else {
    yield put({ type: SAVE_AUDIENCE_FAILURE, message: error.message });
    yield put({
      type: ADD_TOAST,
      id,
      toastProps: {
        status: 'error',
        message: error.message,
      },
    });
  }
}

function* handleOnQueryBuilderOpen(action) {
  const { queryBuilderProps } = action;

  if (queryBuilderProps && queryBuilderProps.audience) {
    const metadata = queryBuilderProps.audience.metadata || {};

    const queryFormatted = mapQueryBlocksWithUniqueIdAndFormat(
      queryBuilderProps.audience.metadata.target_query
    );

    if (metadata.audience_type === 'conversation') {
      const conversationQueries = {};
      const tree = mapConversationQueries(
        queryBuilderProps.audience.metadata.target_query,
        true,
        conversationQueries
      );
      const audience = {
        ...queryBuilderProps.audience,
        metadata: {
          ...queryBuilderProps.audience.metadata,
          target_query: tree,
        },
        queryBuilderType: 'conversation',
      };

      return yield put({
        type: GET_AUDIENCE_SUCCESS,
        audience,
        conversationQueries,
      });
    } else {
      const emptyResponse = { response: { data: [] } };

      const metadataIds = getQueryItemsIds(queryFormatted);
      const encoded = encodeURIComponent(metadataIds.join(','));
      const { response: metadataResponse, error: metadataError } =
        metadataIds.length > 0
          ? yield sagaFetch(
              engineApi.fetchQueryItemsMetadata,
              encoded,
              metadata.data_source
            )
          : emptyResponse;

      const membershipIds = getQueryItemsIds(queryFormatted, 'membership');
      const { response: membershipResponse, error: membershipError } =
        membershipIds.length > 0
          ? yield sagaFetch(api.fetchGetMembershipsApi, membershipIds)
          : emptyResponse;

      if (!metadataError && !membershipError) {
        const audience = {
          ...queryBuilderProps.audience,
          metadata: {
            ...queryBuilderProps.audience.metadata,
            target_query: queryFormatted,
          },
          queryBuilderType: 'default',
        };
        yield put({
          type: GET_QUERY_MEMBERSHIP_REQUEST,
          targetId: audience.id,
          query: audience.metadata,
        });

        return yield put({
          type: GET_AUDIENCE_SUCCESS,
          audience,
          queryEntitiesInfos: [
            ...mergeMetadataWithQuerySources(
              metadataResponse.data,
              queryFormatted
            ),
            ...mapMemberships(membershipResponse.data),
          ],
        });
      }
      return yield put({
        type: GET_AUDIENCE_FAILURE,
        error: true,
        message: metadataError.message,
      });
    }
  }

  yield put({ type: 'NEW_AUDIENCE' });
}

function* fetchLoadMoreRepository(action) {
  const { filter } = action;

  const productDataSources = yield select(getCurrentProductDataSources);
  const source = isEmpty(productDataSources) && 'twitter';
  const currentPagination = yield select(
    (state) => state.repository.pagination
  );
  const currentQ = yield select((state) => state.repository.q);
  const currentFilter = yield select((state) => state.repository.filter);

  const mode = filter || currentFilter;

  const { response, error } = yield sagaFetch(api.fetchRepositoryApi, {
    mode,
    q: currentQ,
    pagination: currentPagination[mode],
    source,
  });

  if (response) {
    yield put({
      type: REPOSITORY_LOAD_MORE_RESULTS_SUCCESS,
      response: response.data,
      total_count: response.total_count,
    });
  } else {
    yield put({ type: REPOSITORY_LOAD_MORE_RESULTS_FAILURE, error });
  }
}

function* handleOpenSaveModal(action) {
  const { data } = action;

  const showModalAction = (selectedItem) => ({
    type: SHOW_MODAL,
    modalProps: {
      isOpen: true,
      position: 'center',
      selectedItem,
    },
    modalType: 'SaveAudience',
  });

  if (data.id) {
    return yield put(showModalAction(data));
  }

  const newAudience = prepareNewAudience(data);
  newAudience.scope = getProductScope();
  const { response, error } = yield sagaFetch(
    api.fetchCreateAudienceApi,
    newAudience
  );

  if (!error) {
    return yield put(showModalAction({ ...data, id: response.data.id }));
  }

  yield put({
    type: ADD_TOAST,
    id: uniqid(),
    toastProps: {
      status: 'error',
      message: 'CREATE_AUDIENCE_ERROR',
    },
  });
}

function* fetchShareAudience(action) {
  const { id, body } = action;
  body.scope = getProductScope();
  const { response, error } = yield sagaFetch(
    api.fetchShareAudienceApi,
    id,
    body
  );

  if (!error) {
    yield put(hideModal());

    yield put(
      addToast({
        status: 'success',
        message: translations['en-US'].TOASTS.SHARE_QUERY,
      })
    );
    return yield put({ type: SHARE_AUDIENCE_SUCCESS, response });
  }
  yield put(
    addToast({
      status: 'error',
      message: translations['en-US'].TOASTS.SHARE_QUERY_ERROR,
    })
  );
  return yield put({ type: SHARE_AUDIENCE_FAILURE, error });
}

function* handleAddAudienceToCompare(action) {
  const { queryData } = action;
  const productDataSources = yield select(getCurrentProductDataSources);
  const filter = !isEmpty(productDataSources) ? 'twitter' : 'recent';
  const audience = {
    ...queryData,
    label: queryData.target_label,
    data_source: 'twitter',
  };
  const newAudience = prepareNewAudience(audience);
  newAudience.scope = getProductScope();
  const { response, error } = yield sagaFetch(
    api.fetchCreateAudienceApi,
    newAudience
  );

  if (!error) {
    yield put({
      type: HIDE_CREATE_AUDIENCE_MODAL,
    });

    yield put(requestRepository(filter));

    const data = {
      label: response.data.label,
      id: response.data.id,
      persistence: 'temporary',
      metadata: newAudience,
    };
    yield put(clearAllQueriesRequest());
    yield put(hideCreateAudienceModal());
    yield put(requestRepository(filter));

    yield put(addApplicationDroppingEntity(data, APPS_MAP.COMPARE));

    return yield put({
      type: ADD_AUDIENCE_TO_COMPARE_SUCCESS,
    });
  }

  return yield put({
    type: ADD_AUDIENCE_TO_COMPARE_FAILURE,
  });
}

function* handleAddDroppingEntity(action) {
  const { appName, audience } = action;

  if (audience.metadata.query_type === 'tsv') {
    yield put({
      type: SET_COMPARE_ERROR,
    });
    return yield put(
      addToast({
        status: 'error',
        message: translations['en-US'].TOASTS.COMPARE_AUDIENCE_NOT_ALLOWED.TSV,
      })
    );
  }
  if (!supportsSegmentationAndCompare(audience?.metadata?.data_source)) {
    yield put({
      type: SET_COMPARE_ERROR,
    });
    return yield put(
      addToast({
        status: 'error',
        message:
          translations['en-US'].TOASTS.COMPARE_AUDIENCE_NOT_ALLOWED[
            audience.metadata.data_source.toUpperCase()
          ],
      })
    );
  }
  const entities = yield select((state) => state.repository.droppingEntities);
  if (!entities[appName]) {
    entities[appName] = [];
  }
  const entityIndx = entities[appName].findIndex((e) => e.id === audience.id);

  if (entityIndx === -1 && entities[appName].length < 3) {
    entities[appName] = [...entities[appName], audience];
  }

  return yield put({
    type: UPDATE_APPLICATION_DROPPING_ENTITIES_REQUEST,
    appName,
    entities: entities[appName],
  });
}

function* handleOpenCompareAfterSave(action) {
  const { audiences } = action;
  const productDataSources = yield select(getCurrentProductDataSources);
  const filter = !isEmpty(productDataSources) ? 'twitter' : 'recent';
  yield put(clearAllQueriesRequest());
  yield put(hideCreateAudienceModal());
  yield put(requestRepository(filter));

  yield put(addApplicationTab([...audiences], APPS_MAP.COMPARE));
}

export function* repositoryActionWatcher() {
  yield takeLatest(GET_REPOSITORY_REQUEST, fetchRepository);
  yield takeEvery(GET_FOLDER_REQUEST, fetchFolder);
  yield takeEvery(GET_ALL_FOLDERS_REQUEST, fetchFolders);
  yield takeEvery(EXPLORE_FOLDER_REQUEST, exploreFolder);
  yield takeLatest(GET_TARGET_FOLDERS_REQUEST, fetchTargetFolders);
  yield takeEvery(GET_TARGET_FOLDER_REQUEST, fetchTargetFolder);
  yield takeEvery(EXPLORE_TARGET_FOLDERS_REQUEST, exploreTargetFolders);
  yield takeEvery(CREATE_FOLDER_REQUEST, fetchCreateFolder);
  yield takeEvery(DELETE_ITEM_REQUEST, fetchDeleteItem);
  yield takeEvery(DELETE_ITEM_SUCCESS, handleDeleteItemSuccess);
  yield takeEvery(CREATE_FOLDER_SUCCESS, handleCreateItemSuccess);
  yield takeEvery(CLONE_AUDIENCE_REQUEST, fetchCloneAudience);
  yield takeEvery(CLONE_AUDIENCE_SUCCESS, handleCreateItemSuccess);
  yield takeEvery(MOVE_ITEM_REQUEST, fetchMoveItem);
  yield takeEvery(MOVE_ITEM_SUCCESS, handleMoveItemSuccess);
  yield takeEvery(RENAME_ITEM_REQUEST, fetchRenameItem);
  yield takeEvery(RENAME_ITEM_SUCCESS, handleRenameItemSuccess);
  yield takeEvery(
    TOGGLE_ITEM_SELECTION_REQUEST,
    handleItemToggleSelectionRequest
  );
  yield takeEvery(
    TOGGLE_ALL_ITEM_SELECTION_REQUEST,
    handleItemToggleAllSelectionRequest
  );
  // yield takeLatest(GET_AUDIENCE_REQUEST, handleGetAudienceRequest); // DO NOT REMOVE, IT WILL BE NEEDED
  yield takeLatest(CREATE_AUDIENCE_REQUEST, handleCreateAudienceRequest);
  yield takeLatest(GET_QUERY_PREVIEW_REQUEST, handleQueryPreviewRequest);
  yield takeLatest(SAVE_AUDIENCE_REQUEST, fetchSaveAudienceRequest);
  yield takeLatest(SHOW_EDIT_AUDIENCE_MODAL, handleOnQueryBuilderOpen);
  yield takeLatest(
    REPOSITORY_LOAD_MORE_RESULTS_REQUEST,
    fetchLoadMoreRepository
  );
  yield takeLatest(OPEN_SAVE_MODAL, handleOpenSaveModal);
  yield takeLatest(SHARE_AUDIENCE_REQUEST, fetchShareAudience);
  yield takeLatest(ADD_AUDIENCE_TO_COMPARE_REQUEST, handleAddAudienceToCompare);
  yield takeLatest(
    ADD_APPLICATION_DROPPING_ENTITY_REQUEST,
    handleAddDroppingEntity
  );
  yield takeLatest(OPEN_COMPARE_AFTER_SAVE, handleOpenCompareAfterSave);
  yield takeLatest(GET_AUDIENCE_SUCCESS, handleGetAudienceSuccess);
}
